import { Link } from "react-router";
import { ArrowRight, FileText, Target, Users, Zap } from "lucide-react";
import { GradientSectionHeading, HEADLINE_GRADIENT } from "../components/GradientSectionHeading";
import { featuredPartnerCards } from "../../data/aboutBrands";
import { BRAND_LOGOS } from "../../data/brandLogos";

export function AboutPage() {
  return (
    <div className="min-h-screen">
      <section className="relative bg-[#F6F2EB] py-40 overflow-hidden h-[768px] flex items-center">
        {/* Animated Background */}
        <div className="absolute inset-0 z-0 pointer-events-none opacity-40">
          <svg width="100%" height="100%" viewBox="0 0 1920 720" preserveAspectRatio="xMidYMid slice" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ shapeRendering: "geometricPrecision" }}>
            <defs>
              {/* Desktop Gradient */}
              <radialGradient id="paint0_radial_desktop" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(960 360) rotate(180) scale(711 897.564)">
                <stop offset="0.436637" stopColor="#F7F4ED"></stop>
                <stop offset="0.727046" stopColor="#FF8819"></stop>
                <stop offset="0.817006" stopColor="#FF339E"></stop>
                <stop offset="0.91787" stopColor="#FFA6F9"></stop>
                <stop offset="1" stopColor="#4E93FF"></stop>
              </radialGradient>
              {/* Mobile Gradient (Scaled down to remain visible within center crop) */}
              <radialGradient id="paint0_radial_mobile" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(960 360) rotate(180) scale(300 400)">
                <stop offset="0.45" stopColor="#F7F4ED"></stop>
                <stop offset="0.8" stopColor="#FF8819"></stop>
                <stop offset="0.9" stopColor="#FF339E"></stop>
                <stop offset="1" stopColor="#FFA6F9"></stop>
                <stop offset="1.5" stopColor="#4E93FF"></stop>
              </radialGradient>
            </defs>
            {/* Desktop Circles (60px gap) */}
            <g id="concentric-circles-desktop" className="hidden md:block" transform="rotate(90 960 360)">
              {Array.from({ length: 25 }, (_, i) => (i + 1) * 60).map((r, i) => {
                const delay = 0.05 * i;
                const circumference = 2 * Math.PI * r;
                return (
                  <circle
                    key={`c-desktop-${r}`}
                    cx="960"
                    cy="360"
                    r={r}
                    stroke="url(#paint0_radial_desktop)"
                    strokeWidth="1.8"
                    fill="none"
                    style={{
                      strokeDasharray: circumference,
                      strokeDashoffset: circumference,
                      animation: `1.5s ease-out ${delay.toFixed(2)}s 1 normal forwards running drawLine`
                    }}
                  />
                );
              })}
            </g>

            {/* Mobile Circles (20px gap for high density in small crops) */}
            <g id="concentric-circles-mobile" className="block md:hidden" transform="rotate(90 960 360)">
              {Array.from({ length: 30 }, (_, i) => (i + 1) * 20).map((r, i) => {
                const delay = 0.05 * i;
                const circumference = 2 * Math.PI * r;
                return (
                  <circle
                    key={`c-mobile-${r}`}
                    cx="960"
                    cy="360"
                    r={r}
                    stroke="url(#paint0_radial_mobile)"
                    strokeWidth="1.2"
                    fill="none"
                    style={{
                      strokeDasharray: circumference,
                      strokeDashoffset: circumference,
                      animation: `1.5s ease-out ${delay.toFixed(2)}s 1 normal forwards running drawLine`
                    }}
                  />
                );
              })}
            </g>
          </svg>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col items-center text-center">
            <div className="relative max-w-4xl">
              {/* Decorative Elements */}
              <div className="absolute -left-[185px] top-[80px] hidden h-[80px] w-[80px] overflow-hidden rounded-full bg-[#FBF7F4] shadow-sm lg:flex border border-border/50" style={{ zIndex: 0 }}>
                <img src="/left1.png" alt="Premium custom packaging sample — Colour Club Printers Faisalabad" className="h-full w-full object-cover" />
              </div>
              <div className="absolute -right-[50px] top-[10px] hidden h-[80px] w-[80px] overflow-hidden rounded-full bg-[#FBF7F4] shadow-sm lg:flex border border-border/50" style={{ zIndex: 1 }}>
                <img src="/right1.png" alt="Custom printed packaging box — Colour Club Printers Pakistan" className="h-full w-full object-cover" />
              </div>
              <div className="absolute -left-[75px] -top-[10px] hidden h-[96px] w-[96px] overflow-hidden rounded-full bg-[#FBF7F4] shadow-sm lg:flex border border-border/50" style={{ zIndex: 2 }}>
                <img src="/left2.png" alt="Luxury packaging with foil stamping — printing press Faisalabad" className="h-full w-full object-cover" />
              </div>
              <div className="absolute -right-[100px] top-[130px] hidden h-[64px] w-[64px] overflow-hidden rounded-full bg-[#FBF7F4] shadow-sm lg:flex border border-border/50" style={{ zIndex: 3 }}>
                <img src="/right2.png" alt="Garment hang tags and labels — packaging manufacturer Pakistan" className="h-full w-full object-cover" />
              </div>
              <div className="absolute -left-[105px] top-[165px] hidden h-[64px] w-[64px] overflow-hidden rounded-full bg-[#FBF7F4] shadow-sm lg:flex border border-border/50" style={{ zIndex: 4 }}>
                <img src="/left3.png" alt="Eco-friendly kraft packaging — FSC certified Colour Club Printers" className="h-full w-full object-cover" />
              </div>
              <div className="absolute -right-[220px] top-[50px] hidden h-[96px] w-[96px] overflow-hidden rounded-full bg-[#FBF7F4] shadow-sm lg:flex border border-border/50" style={{ zIndex: 5 }}>
                <img src="/right3.png" alt="Retail display packaging — custom boxes Faisalabad Pakistan" className="h-full w-full object-cover" />
              </div>

              <div className="mx-auto mb-6 flex h-[37px] w-max whitespace-nowrap grow-0 order-0 flex-row items-center justify-center gap-[8px] rounded-full bg-[#FBF7F4] pb-[8px] px-4 pt-[8px] isolate text-[12.8px] font-bold text-[#131312] shadow-[0_4px_12px_-4px_rgba(0,0,0,0.15)]">
                <span>Est. 1995</span>
              </div>
              <GradientSectionHeading
                as="h1"
                className="mb-6 text-[48px] sm:text-[60px] md:text-[70px] lg:text-[78.1px] font-[700] leading-[1.1] [text-wrap:wrap]"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                About
                <br />
                Colour Club
                <br className="hidden md:block lg:hidden" />
                {" "}Printers
              </GradientSectionHeading>
              <p className="mx-auto flex h-auto w-full max-w-[600px] grow-0 flex-col items-center px-[7.6px] text-center text-[18.8px] font-bold text-[#131312] transition-colors duration-300 mb-10">
                Three decades of excellence in packaging and printing,
                <br />
                serving global brands with premium solutions and
                <br />
                unwavering commitment to quality
              </p>
              <div className="mx-auto flex h-auto flex-col sm:flex-row items-center justify-center gap-4 mt-6">
                <Link
                  to="/quote"
                  className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[#131312] text-sm font-medium text-white transition-colors hover:bg-[#1c1c1b] shadow-[0_4px_6px_-4px_rgba(0,0,0,0.22),0_10px_15px_-3px_rgba(0,0,0,0.18)] sm:h-14 sm:w-auto sm:px-8 sm:text-base"
                >
                  Start Your Project
                </Link>
                <Link
                  to="/products"
                  className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-[#131312]/20 bg-[#FBFAF7] text-sm font-medium text-[#131312] transition-colors hover:bg-[#F2ECE4] sm:h-14 sm:w-auto sm:px-8 sm:text-base"
                >
                  Browse Products
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:items-stretch max-w-6xl mx-auto">
            {/* Left Column: Image Container */}
            <div
              className="relative order-2 lg:order-1 overflow-hidden w-full lg:h-full"
              style={{
                background: "#F3F4F6",
                borderRadius: "16px",
                padding: "0px",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                minHeight: "400px" // fallback for mobile
              }}
            >
              <img
                src="/story.png"
                alt="Colour Club Printers heritage — 30 years of premium packaging manufacturing in Faisalabad Pakistan"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>

            {/* Right Column: Text Container */}
            <div
              className="flex flex-col items-start order-1 lg:order-2 w-full"
              style={{
                minHeight: "auto",
                gap: "24px",
              }}
            >
              <h2
                style={{
                  width: "100%",
                  minHeight: "65px",
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(40px, 10vw, 56px)",
                  lineHeight: "1.1",
                  letterSpacing: "-1.12px",
                  margin: 0
                }}
              >
                <span className="gradient-text-animate gradient-text-animate--play" style={{ backgroundImage: HEADLINE_GRADIENT }}>
                  Our Story
                </span>
              </h2>
              <div
                className="flex flex-col items-start"
                style={{
                  gap: "24px",
                  width: "100%",
                }}
              >
                <p
                  className="text-[#131312]/70"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                  }}
                >
                  Founded in 1995 as Paper Printing Services, we began our journey with a simple mission: to deliver exceptional quality in every print. Over three decades, we've evolved into a global packaging leader, trusted by some of the world's most prestigious brands.
                </p>
                <p
                  className="text-[#131312]/70"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                  }}
                >
                  Today, we specialize in premium packaging solutions for textile, fashion, cosmetics, and e-commerce industries. Our commitment to sustainability and quality is reflected in our FSC, ISO 9001, and Sedex certifications.
                </p>
                <p
                  className="text-[#131312]/70"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                  }}
                >
                  With state-of-the-art facilities spanning 22,500 square feet and production running Monday to Saturday, 9 AM to 6 PM, we produce over 50 million units annually while maintaining our founding principle: unwavering dedication to excellence.
                </p>
                <div className="mt-4 p-6 bg-[#F6F2EB] rounded-2xl border border-[#131312]/5 w-full">
                  <h3 className="font-semibold text-lg mb-2 text-[#131312]">Our company structure</h3>
                  <p className="text-[#131312]/70 text-sm">
                    Colour Club Printers is the customer-facing commercial brand operated by Paper Printing Services. All quotations, contracts, invoices, banking transactions and FSC-certified sales are issued by Paper Printing Services.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F6F2EB] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <GradientSectionHeading className="mb-6 text-4xl md:text-6xl font-semibold leading-[1.1] tracking-tight">
              Our Mission & Vision
            </GradientSectionHeading>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="rounded-3xl border border-border bg-[#FBFAF7] p-12">
              <div className="icon-box flex-shrink-0 size-16 mb-6">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 0C20.5304 0 21.0391 0.210714 21.4142 0.585786C21.7893 0.960859 22 1.46957 22 2C22 2.53043 21.7893 3.03914 21.4142 3.41421C21.0391 3.78929 20.5304 4 20 4C16.8355 4 13.7421 4.93838 11.1109 6.69649C8.47969 8.45459 6.42893 10.9534 5.21793 13.8771C4.00693 16.8007 3.69007 20.0177 4.30744 23.1214C4.9248 26.2251 6.44865 29.0761 8.68629 31.3137C10.9239 33.5513 13.7749 35.0752 16.8786 35.6926C19.9823 36.3099 23.1993 35.9931 26.1229 34.7821C29.0466 33.5711 31.5454 31.5203 33.3035 28.8891C35.0616 26.2579 36 23.1645 36 20C36 19.4696 36.2107 18.9609 36.5858 18.5858C36.9609 18.2107 37.4696 18 38 18C38.5304 18 39.0391 18.2107 39.4142 18.5858C39.7893 18.9609 40 19.4696 40 20C40 31.046 31.046 40 20 40C8.954 40 0 31.046 0 20C0 8.954 8.954 0 20 0ZM20 8C20.5304 8 21.0391 8.21071 21.4142 8.58579C21.7893 8.96086 22 9.46957 22 10C22 10.5304 21.7893 11.0391 21.4142 11.4142C21.0391 11.7893 20.5304 12 20 12C18.4177 12 16.871 12.4692 15.5554 13.3482C14.2398 14.2273 13.2145 15.4767 12.609 16.9385C12.0035 18.4003 11.845 20.0089 12.1537 21.5607C12.4624 23.1126 13.2243 24.538 14.3431 25.6569C15.462 26.7757 16.8874 27.5376 18.4393 27.8463C19.9911 28.155 21.5997 27.9965 23.0615 27.391C24.5233 26.7855 25.7727 25.7602 26.6518 24.4446C27.5308 23.129 28 21.5823 28 20C28 19.4696 28.2107 18.9609 28.5858 18.5858C28.9609 18.2107 29.4696 18 30 18C30.5304 18 31.0391 18.2107 31.4142 18.5858C31.7893 18.9609 32 19.4696 32 20C32 22.3734 31.2962 24.6935 29.9776 26.6668C28.6591 28.6402 26.7849 30.1783 24.5922 31.0866C22.3995 31.9948 19.9867 32.2324 17.6589 31.7694C15.3311 31.3064 13.1929 30.1635 11.5147 28.4853C9.83649 26.8071 8.6936 24.6689 8.23058 22.3411C7.76755 20.0133 8.00519 17.6005 8.91345 15.4078C9.8217 13.2151 11.3598 11.3409 13.3332 10.0224C15.3065 8.70379 17.6266 8 20 8ZM33.142 0.2C33.4072 0.2 33.6616 0.305357 33.8491 0.492893C34.0366 0.68043 34.142 0.934783 34.142 1.2V4.86C34.1425 5.12487 34.2481 5.37871 34.4356 5.56581C34.6231 5.75292 34.8771 5.858 35.142 5.858H38.8C39.0652 5.858 39.3196 5.96336 39.5071 6.15089C39.6946 6.33843 39.8 6.59278 39.8 6.858V8.688L36.024 12.464C35.274 13.2142 34.2568 13.6358 33.196 13.636H29.196L21.416 21.414C21.0388 21.7783 20.5336 21.9799 20.0092 21.9753C19.4848 21.9708 18.9832 21.7605 18.6124 21.3896C18.2415 21.0188 18.0312 20.5172 18.0267 19.9928C18.0221 19.4684 18.2237 18.9632 18.588 18.586L26.368 10.808V6.808C26.3677 5.74751 26.7885 4.7303 27.538 3.98L31.318 0.2H33.142Z" fill="currentColor"/>
                </svg>
              </div>
              <h3 className="text-2xl font-semibold mb-6">Our Mission</h3>
              <p className="text-lg text-muted-foreground">
                To deliver world-class packaging solutions that elevate brands and exceed expectations. We combine cutting-edge technology, sustainable practices, and expert craftsmanship to create packaging that tells your brand's story while protecting our planet.
              </p>
            </div>
            <div className="rounded-3xl border border-border bg-[#FBFAF7] p-12">
              <div className="icon-box flex-shrink-0 size-16 mb-6">
                <svg width="34" height="40" viewBox="0 0 34 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M25.285 2.14607C21.3769 -0.0344954 16.7781 -0.578841 12.477 0.630033C8.17593 1.83891 4.51595 4.70449 2.28372 8.61087C0.051494 12.5172 -0.574775 17.1526 0.539523 21.5205C1.65382 25.8885 4.41973 29.6404 8.24275 31.9698V38.212C8.24275 38.4468 8.28852 38.6793 8.37745 38.8962C8.46639 39.1132 8.59674 39.3103 8.76106 39.4763C8.92539 39.6423 9.12047 39.774 9.33517 39.8639C9.54987 39.9538 9.77998 40 10.0124 40C10.2448 40 10.4749 39.9538 10.6896 39.8639C10.9043 39.774 11.0994 39.6423 11.2637 39.4763C11.428 39.3103 11.5584 39.1132 11.6473 38.8962C11.7362 38.6793 11.782 38.4468 11.782 38.212V30.917C11.7808 30.5879 11.6905 30.2654 11.5208 29.9845C11.3511 29.7036 11.1085 29.475 10.8193 29.3235C8.68087 28.2217 6.8717 26.5639 5.5781 24.5208C4.28449 22.4778 3.55319 20.1233 3.45948 17.6997C3.36578 15.2762 3.91306 12.8712 5.04498 10.7324C6.17691 8.5936 7.85256 6.79825 9.89942 5.53121C11.9463 4.26417 14.2904 3.57122 16.6904 3.52371C19.0904 3.4762 21.4596 4.07584 23.554 5.2609C25.6484 6.44596 27.3924 8.17361 28.6064 10.266C29.8203 12.3583 30.4604 14.7398 30.4607 17.1652V28.3909C30.4607 28.661 30.3546 28.9201 30.1655 29.1111C29.9765 29.3021 29.7201 29.4094 29.4528 29.4094H23.8976C23.4282 29.4094 22.9781 29.5977 22.6462 29.9331C22.3144 30.2684 22.1279 30.7232 22.1279 31.1974V38.212C22.1279 38.6862 22.3144 39.141 22.6462 39.4763C22.9781 39.8116 23.4282 40 23.8976 40C24.3669 40 24.817 39.8116 25.1489 39.4763C25.4807 39.141 25.6672 38.6862 25.6672 38.212V32.9854H29.4528C30.0499 32.9854 30.6412 32.8665 31.1929 32.6356C31.7446 32.4047 32.2459 32.0663 32.6681 31.6397C33.0904 31.213 33.4253 30.7066 33.6539 30.1491C33.8824 29.5917 34 28.9943 34 28.3909V17.1681C33.9994 14.1076 33.1917 11.1025 31.6599 8.46223C30.1281 5.82192 27.9276 3.64174 25.285 2.14607ZM12.3539 8.80312C14.3333 7.65032 16.6461 7.22395 18.9009 7.59617C21.1556 7.9684 23.2139 9.11635 24.7272 10.8457C25.7465 12.0101 25.4633 13.6836 24.4157 14.6363C21.3351 17.4484 16.2301 21.0931 11.1563 22.4519C9.86514 22.7952 8.44944 22.1659 7.9766 20.8213C7.20261 18.6173 7.21661 16.209 8.01616 14.0143C8.81572 11.8197 10.3504 9.97413 12.3539 8.80312Z" fill="currentColor"/>
                </svg>
              </div>
              <h3 className="text-2xl font-semibold mb-6">Our Vision</h3>
              <p className="text-lg text-muted-foreground">
                To be the most trusted packaging partner for global brands, recognized for innovation, sustainability, and uncompromising quality. We envision a future where every package we produce contributes to a more sustainable world while delivering exceptional brand experiences.
              </p>
            </div>
          </div>
          <div className="mt-12 flex justify-center">
            <a
              href="/Company-Profile.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-12 items-center justify-center gap-2.5 rounded-xl bg-[#131312] px-8 text-sm font-medium text-white transition-all duration-200 hover:bg-[#1c1c1b] hover:shadow-lg shadow-[0_4px_6px_-4px_rgba(0,0,0,0.22),0_10px_15px_-3px_rgba(0,0,0,0.18)] sm:h-14 sm:text-base group"
            >
              <FileText className="size-5 transition-transform group-hover:scale-110" />
              <span>View Company Profile</span>
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <GradientSectionHeading className="mb-6 text-4xl md:text-6xl font-semibold leading-[1.1] tracking-tight">
              Leadership Team
            </GradientSectionHeading>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Visionary leaders driving innovation and excellence in packaging
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-16">
            {/* CEO */}
            <div className="relative w-full min-h-[480px] rounded-3xl overflow-hidden border border-border">
              <img
                src="/suhail.png"
                alt="Muhammad Suhail, Chief Executive Officer"
                className="absolute inset-0 h-full w-full object-cover object-top"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <h3 className="text-2xl font-semibold text-white/90 mb-1">Muhammad Suhail</h3>
                <div className="text-white/60 font-medium">Chief Executive Officer</div>
              </div>
            </div>

            {/* MD */}
            <div className="relative w-full min-h-[480px] rounded-3xl overflow-hidden border border-border">
              <img
                src="/saram.png"
                alt="Muhammad Saram Suhail, Managing Director"
                className="absolute inset-0 h-full w-full object-cover object-top"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <h3 className="text-2xl font-semibold text-white/90 mb-1">Muhammad Saram Suhail</h3>
                <div className="text-white/60 font-medium">Managing Director</div>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="flex w-full flex-col items-center gap-12 rounded-3xl border border-border bg-[#F6F2EB] px-12 pt-12 pb-0">
              <div className="w-full text-center">
                <h3 className="mb-4 text-2xl font-semibold">Expert Teams</h3>
                <p className="mx-auto max-w-3xl text-lg text-muted-foreground">
                  Our leadership is supported by talented professionals across design, production, and quality control
                </p>
              </div>
              <div className="grid w-full grid-cols-1 gap-6 pb-12 md:grid-cols-3">
                <div className="flex flex-col items-center rounded-2xl border border-border bg-[#FBFAF7] px-6 pb-6 pt-6 text-center">
                  <div className="mx-auto mb-4 flex size-20 shrink-0 flex-row items-center justify-center rounded-full bg-[#F6F2EB] px-5">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.4998 11.6667C22.9601 11.6667 23.3332 11.2936 23.3332 10.8333C23.3332 10.3731 22.9601 10 22.4998 10C22.0396 10 21.6665 10.3731 21.6665 10.8333C21.6665 11.2936 22.0396 11.6667 22.4998 11.6667Z" fill="#131312" stroke="#131312" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M29.1668 18.3334C29.6271 18.3334 30.0002 17.9603 30.0002 17.5001C30.0002 17.0398 29.6271 16.6667 29.1668 16.6667C28.7066 16.6667 28.3335 17.0398 28.3335 17.5001C28.3335 17.9603 28.7066 18.3334 29.1668 18.3334Z" fill="#131312" stroke="#131312" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14.1668 13.3334C14.6271 13.3334 15.0002 12.9603 15.0002 12.5001C15.0002 12.0398 14.6271 11.6667 14.1668 11.6667C13.7066 11.6667 13.3335 12.0398 13.3335 12.5001C13.3335 12.9603 13.7066 13.3334 14.1668 13.3334Z" fill="#131312" stroke="#131312" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10.8333 21.6667C11.2936 21.6667 11.6667 21.2936 11.6667 20.8333C11.6667 20.3731 11.2936 20 10.8333 20C10.3731 20 10 20.3731 10 20.8333C10 21.2936 10.3731 21.6667 10.8333 21.6667Z" fill="#131312" stroke="#131312" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M20.0002 3.33325C10.8335 3.33325 3.3335 10.8333 3.3335 19.9999C3.3335 29.1666 10.8335 36.6666 20.0002 36.6666C21.5435 36.6666 22.7468 35.4233 22.7468 33.8533C22.7468 33.1249 22.4468 32.4616 22.0185 31.9783C21.5352 31.4966 21.2885 30.8916 21.2885 30.1033C21.2822 29.7364 21.3498 29.3721 21.4872 29.032C21.6247 28.6918 21.8292 28.3828 22.0887 28.1234C22.3481 27.864 22.6571 27.6595 22.9972 27.522C23.3373 27.3845 23.7017 27.3169 24.0685 27.3232H27.3952C32.4802 27.3232 36.6535 23.1516 36.6535 18.0666C36.6085 10.0199 29.1018 3.33325 20.0002 3.33325Z" stroke="#131312" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h4 className="mb-2 max-w-[245px] text-base font-bold leading-6 text-[#0A0A0A]">
                    Design Team
                  </h4>
                  <p className="max-w-[245px] text-sm leading-5 text-muted-foreground">
                    Expert packaging designers and structural engineers
                  </p>
                </div>
                <div className="flex flex-col items-center rounded-2xl border border-border bg-[#FBFAF7] px-6 pb-6 pt-6 text-center">
                  <div className="mx-auto mb-4 flex size-20 shrink-0 flex-row items-center justify-center rounded-full bg-[#F6F2EB] px-5">
                    <svg width="41" height="41" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19.5299 0.314503C19.7187 0.206637 19.9324 0.149902 20.1499 0.149902C20.3674 0.149902 20.5811 0.206637 20.7699 0.314503L30.1449 5.67211C30.3362 5.7814 30.4953 5.93931 30.6059 6.12985C30.7165 6.3204 30.7748 6.5368 30.7749 6.75713V16.7448L39.5199 21.7449C39.7105 21.8538 39.869 22.0109 39.9796 22.2005C40.0902 22.3901 40.149 22.6055 40.1499 22.825V33.5402C40.1498 33.7605 40.0915 33.9769 39.9809 34.1674C39.8703 34.358 39.7112 34.5159 39.5199 34.6252L30.1449 39.9828C29.9561 40.0907 29.7424 40.1474 29.5249 40.1474C29.3074 40.1474 29.0937 40.0907 28.9049 39.9828L20.1499 34.9827L11.3949 39.9853C11.2061 40.0932 10.9924 40.1499 10.7749 40.1499C10.5574 40.1499 10.3437 40.0932 10.1549 39.9853L0.779902 34.6277C0.588585 34.5184 0.429552 34.3605 0.318916 34.1699C0.20828 33.9794 0.149973 33.763 0.149902 33.5427L0.149902 22.8275C0.149973 22.6071 0.20828 22.3907 0.318916 22.2002C0.429552 22.0096 0.588585 21.8517 0.779902 21.7424L9.5249 16.7448V6.75713C9.52497 6.5368 9.58328 6.3204 9.69392 6.12985C9.80455 5.93931 9.96358 5.7814 10.1549 5.67211L19.5299 0.314503ZM10.7749 18.9099L3.9199 22.8275L10.7749 26.745L17.6299 22.8275L10.7749 18.9099ZM18.8999 24.9825L12.0249 28.9101V36.7452L18.8999 32.8177V24.9825ZM21.3999 32.8177L28.2749 36.7452V28.9101L21.3999 24.9825V32.8177ZM22.6699 22.8275L29.5249 26.745L36.3799 22.8275L29.5249 18.9099L22.6699 22.8275ZM28.2749 16.7448V8.90968L21.3999 12.8398V20.6749L28.2749 16.7448ZM18.8999 20.6749V12.8398L12.0249 8.90968V16.7448L18.8999 20.6749ZM13.2949 6.75713L20.1499 10.6747L27.0049 6.75713L20.1499 2.83955L13.2949 6.75713ZM37.6499 24.9825L30.7749 28.9101V36.7452L37.6499 32.8177V24.9825ZM9.5249 36.7452V28.9101L2.6499 24.9825V32.8177L9.5249 36.7452Z" fill="#131312" stroke="#131312" strokeWidth="0.3"/>
                    </svg>
                  </div>
                  <h4 className="mb-2 max-w-[245px] text-base font-bold leading-6 text-[#0A0A0A]">
                    Production Team
                  </h4>
                  <p className="max-w-[245px] text-sm leading-5 text-muted-foreground">
                    Skilled technicians and print specialists
                  </p>
                </div>
                <div className="flex flex-col items-center rounded-2xl border border-border bg-[#FBFAF7] px-6 pb-6 pt-6 text-center">
                  <div className="mx-auto mb-4 flex size-20 shrink-0 flex-row items-center justify-center rounded-full bg-[#F6F2EB] px-5">
                    <svg width="30" height="40" viewBox="0 0 30 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.6406 22.2857L12.2812 16.8571L7.96875 13.3333H13.3125L15 8L16.6875 13.3333H22.0312L17.6719 16.8571L19.3125 22.2857L15 18.9048L10.6406 22.2857ZM3.75 40V25.2857C2.5625 23.9524 1.64063 22.4286 0.984375 20.7143C0.328125 19 0 17.1746 0 15.2381C0 10.9841 1.45312 7.38095 4.35937 4.42857C7.26562 1.47619 10.8125 0 15 0C19.1875 0 22.7344 1.47619 25.6406 4.42857C28.5469 7.38095 30 10.9841 30 15.2381C30 17.1746 29.6719 19 29.0156 20.7143C28.3594 22.4286 27.4375 23.9524 26.25 25.2857V40L15 36.1905L3.75 40ZM22.9688 23.3333C25.1562 21.1111 26.25 18.4127 26.25 15.2381C26.25 12.0635 25.1562 9.36508 22.9688 7.14286C20.7812 4.92064 18.125 3.80952 15 3.80952C11.875 3.80952 9.21875 4.92064 7.03125 7.14286C4.84375 9.36508 3.75 12.0635 3.75 15.2381C3.75 18.4127 4.84375 21.1111 7.03125 23.3333C9.21875 25.5556 11.875 26.6667 15 26.6667C18.125 26.6667 20.7812 25.5556 22.9688 23.3333ZM7.5 34.3333L15 32.381L22.5 34.3333V28.4286C21.4062 29.0635 20.2263 29.5638 18.96 29.9295C17.6938 30.2952 16.3738 30.4775 15 30.4762C13.6263 30.4749 12.3056 30.2921 11.0381 29.9276C9.77063 29.5632 8.59125 29.0641 7.5 28.4305V34.3333Z" fill="#131312"/>
                    </svg>
                  </div>
                  <h4 className="mb-2 max-w-[245px] text-base font-bold leading-6 text-[#0A0A0A]">
                    Quality Control
                  </h4>
                  <p className="max-w-[245px] text-sm leading-5 text-muted-foreground">
                    Dedicated QC team ensuring perfection
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F6F2EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex h-[36px] items-center justify-center rounded-full border border-[rgba(0,0,0,0.08)] bg-[#FBFAF7] px-5 text-sm font-medium text-[#131312] mb-6">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2">
                <g clipPath="url(#clip0_420_10265)">
                  <path d="M4 14.6666V2.66659C4 2.31296 4.14048 1.97382 4.39052 1.72378C4.64057 1.47373 4.97971 1.33325 5.33333 1.33325H10.6667C11.0203 1.33325 11.3594 1.47373 11.6095 1.72378C11.8595 1.97382 12 2.31296 12 2.66659V14.6666H4Z" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3.99967 8H2.66634C2.31272 8 1.97358 8.14048 1.72353 8.39052C1.47348 8.64057 1.33301 8.97971 1.33301 9.33333V13.3333C1.33301 13.687 1.47348 14.0261 1.72353 14.2761C1.97358 14.5262 2.31272 14.6667 2.66634 14.6667H3.99967" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 6H13.3333C13.687 6 14.0261 6.14048 14.2761 6.39052C14.5262 6.64057 14.6667 6.97971 14.6667 7.33333V13.3333C14.6667 13.687 14.5262 14.0261 14.2761 14.2761C14.0261 14.5262 13.687 14.6667 13.3333 14.6667H12" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.66699 4H9.33366" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.66699 6.66675H9.33366" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.66699 9.33325H9.33366" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.66699 12H9.33366" stroke="#131312" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                </g>
                <defs>
                  <clipPath id="clip0_420_10265">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              Our Facility
            </div>
            <GradientSectionHeading className="text-4xl md:text-6xl font-semibold leading-[1.1] tracking-tight mb-6">
              State-of-the-Art Facility
            </GradientSectionHeading>
            <p className="text-xl text-[#131312] max-w-3xl mx-auto">
              22,500 sq ft of advanced production capabilities, cutting-edge equipment, and dedicated expertise
            </p>
          </div>

          <div className="flex flex-col gap-6 mb-16">
            <div className="relative rounded-3xl overflow-hidden h-[500px] group w-full">
              <img
                src="/facility1.png"
                alt="Modern industrial facility exterior"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <h3 className="text-xl md:text-4xl lg:text-5xl font-bold mb-2 text-[#FBFAF7]">22,500 sq ft Production Facility</h3>
                <p className="text-white/90 text-sm md:text-base">Purpose-built for precision manufacturing with operations from Mon–Sat (9 AM - 6 PM) and world-class quality control</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="relative rounded-3xl overflow-hidden h-[327px] group">
                <img
                  src="/facility2.png"
                  alt="Industrial printing press"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-6 left-6 text-white pr-6">
                  <h3 className="text-xl font-bold mb-1 text-[#FBFAF7]">Advanced Printing Presses</h3>
                  <p className="text-white/90 text-sm">15+ state-of-the-art printing lines</p>
                </div>
              </div>
              <div className="relative rounded-3xl overflow-hidden h-[327px] group">
                <img
                  src="/inside3.png"
                  alt="Manufacturing equipment"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-6 left-6 text-white">
                </div>
              </div>
              <div className="relative rounded-3xl overflow-hidden h-[327px] group">
                <img
                  src="/inside5.png"
                  alt="Expert packaging team"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-6 left-6 text-white">
                </div>
              </div>
            </div>
            <div className="mt-6 flex w-full justify-center">
              <Link
                to="/facility"
                className="inline-flex h-[56px] w-[212.71px] items-center justify-center gap-2 rounded-[12px] bg-[#131312] text-[#FBFAF7] font-medium transition-colors hover:bg-black/90 shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)]"
              >
                View Our Facility
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.16699 10H15.8337" stroke="white" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M10 4.1665L15.8333 9.99984L10 15.8332" stroke="white" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </div>
          </div>
          </div>
        </div>
      </section>

      <section className="bg-[#FBFAF7] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <GradientSectionHeading className="mb-6 text-4xl md:text-6xl font-semibold leading-[1.1] tracking-tight">
              Trusted by 500+ Leading Brands
            </GradientSectionHeading>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              We're proud to partner with some of the UK's most recognized brands across textile, retail, and e-commerce
            </p>
          </div>

          <div className="mb-16">
            <div className="text-center mb-8">
              <div className="mx-auto mb-4 inline-flex h-9 w-[150.66px] shrink-0 grow-0 items-center justify-center rounded-full border border-[rgba(0,0,0,0.08)] bg-[#F6F2EB] text-sm font-medium text-[#0A0A0A]">
                Featured Partners
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {featuredPartnerCards.map((partner) => (
                <div
                  key={partner.alt}
                  className="w-full rounded-3xl border border-border bg-[#F6F2EB] p-10 text-center transition-all hover:shadow-2xl"
                >
                  <div className="mb-4 flex min-h-[3.5rem] items-center justify-center">
                    <img
                      src={partner.logo}
                      alt={partner.alt}
                      className="max-h-14 w-full max-w-[200px] object-contain object-center"
                      width={200}
                      height={56}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="text-sm font-medium text-[#0A0A0A] mb-4">{partner.role}</div>
                  <p className="text-sm text-muted-foreground">{partner.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-border bg-[#F6F2EB] p-12">
            <div className="text-center mb-10">
              <h3 className="text-2xl font-semibold mb-3">And Many More Industry Leaders</h3>
              <p className="text-muted-foreground">Brands across fashion, retail, cosmetics, and e-commerce trust us with their packaging</p>
            </div>
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4">
              {BRAND_LOGOS.map((brand) => (
                <div
                  key={brand.src}
                  className="flex min-h-[88px] items-center justify-center rounded-2xl border border-gray-100 bg-white p-6 transition-all hover:shadow-lg"
                >
                  <img
                    src={brand.src}
                    alt={brand.alt}
                    className="max-h-12 w-full max-w-[140px] object-contain object-center"
                    width={140}
                    height={48}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
